import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    ArrowBackRounded,
    CheckRounded,
    CloseRounded,
    DeleteOutlineRounded,
    EditRounded,
} from '@mui/icons-material';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import type { UIMessage } from 'ai';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    AndieChatMessagesDocument,
    AndieChatThreadsDocument,
    DeleteAndieChatThreadDocument,
    UpdateAndieChatThreadTitleDocument,
    type AndieChatThreadsQuery,
} from '@/graphql/generated/andie-history.generated';
import { useAndie } from '@/providers/andie-provider';
import {
    useAndieChatControls,
    useAndieChatMessages,
    type HarnessPlanData,
} from '@/providers/andie-chat-provider';

type AndieHistoryThread = AndieChatThreadsQuery['andieChatThreads']['threads'][number];

const CHAT_HISTORY_ERROR_FALLBACK = 'Something went wrong. Please try again.';
const CHAT_HISTORY_TITLE_MAX_LENGTH = 80;

const getThreadTitle = (thread: AndieHistoryThread, t: ReturnType<typeof useTranslation>['t']) => {
    const title = thread.title?.trim();
    return title || t('andie.chat.history.new-chat', { defaultValue: 'New chat' });
};

type AndieChatHistoryProps = {
    onClose: () => void;
    onNewChat: () => void;
};

const COLLAPSED_THREAD_LIMIT = 20;

const AndieChatHistory = ({ onClose, onNewChat }: AndieChatHistoryProps) => {
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { processingFirstMessage, inputValueRef } = useAndie();
    const { chatId } = useAndieChatMessages();
    const { setMessages, setThreadId, hydrateHarnessPlan, isLoading, stop } = useAndieChatControls();
    const [threadToDelete, setThreadToDelete] = useState<AndieHistoryThread | null>(null);
    const [showAllThreads, setShowAllThreads] = useState(false);
    const [editingThreadId, setEditingThreadId] = useState<string | null>(null);
    const [editingTitle, setEditingTitle] = useState('');

    const { data, loading, error } = useQuery(AndieChatThreadsDocument, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const [loadMessages, { loading: loadingMessages }] = useLazyQuery(AndieChatMessagesDocument, {
        fetchPolicy: 'network-only',
    });

    const [deleteThread, { loading: deletingThread }] = useMutation(DeleteAndieChatThreadDocument, {
        refetchQueries: [AndieChatThreadsDocument],
        awaitRefetchQueries: true,
        onError: (deleteError) =>
            enqueueSnackbar(t(deleteError.message, { defaultValue: CHAT_HISTORY_ERROR_FALLBACK })),
    });

    const [updateThreadTitle, { loading: updatingThreadTitle }] = useMutation(
        UpdateAndieChatThreadTitleDocument,
        {
            refetchQueries: [AndieChatThreadsDocument],
            update: (cache, { data: updateData }) => {
                const updatedThread = updateData?.updateAndieChatThreadTitle;
                if (!updatedThread) return;

                const existing = cache.readQuery<AndieChatThreadsQuery>({
                    query: AndieChatThreadsDocument,
                });
                if (!existing) return;

                cache.writeQuery<AndieChatThreadsQuery>({
                    query: AndieChatThreadsDocument,
                    data: {
                        ...existing,
                        andieChatThreads: {
                            ...existing.andieChatThreads,
                            threads: existing.andieChatThreads.threads.map((thread) =>
                                thread.id === updatedThread.id ? { ...thread, ...updatedThread } : thread,
                            ),
                        },
                    },
                });
            },
        },
    );

    const threads = data?.andieChatThreads.threads ?? [];
    const visibleThreads = showAllThreads ? threads : threads.slice(0, COLLAPSED_THREAD_LIMIT);
    const hasMoreThreads = threads.length > COLLAPSED_THREAD_LIMIT;
    const dateFormatter = useMemo(
        () =>
            new Intl.DateTimeFormat(i18n.language, {
                dateStyle: 'medium',
                timeStyle: 'short',
            }),
        [i18n.language],
    );

    const handleSelectThread = async (thread: AndieHistoryThread) => {
        if (loadingMessages || thread.id === chatId) {
            onClose();
            return;
        }

        try {
            if (isLoading) {
                stop();
            }
            const result = await loadMessages({ variables: { threadId: thread.id } });
            const messages = result.data?.andieChatMessages.messages;

            setThreadId(thread.id);
            setMessages(Array.isArray(messages) ? (messages as UIMessage[]) : []);
            hydrateHarnessPlan((result.data?.andieChatMessages.plan as HarnessPlanData | null) ?? null);
            processingFirstMessage.current = false;
            inputValueRef.current = '';
            onClose();
        } catch (selectError) {
            const message = selectError instanceof Error ? selectError.message : 'error.something-wrong';
            enqueueSnackbar(t(message, { defaultValue: CHAT_HISTORY_ERROR_FALLBACK }));
        }
    };

    const handleConfirmDelete = async () => {
        if (!threadToDelete) return;

        const deletingActiveThread = threadToDelete.id === chatId;
        if (deletingActiveThread && isLoading) {
            stop();
        }
        const nextThread = threads.find((thread) => thread.id !== threadToDelete.id);
        await deleteThread({ variables: { threadId: threadToDelete.id } });
        setThreadToDelete(null);

        if (deletingActiveThread) {
            if (nextThread) {
                await handleSelectThread(nextThread);
                return;
            }

            onNewChat();
        }
    };

    const handleStartRename = (thread: AndieHistoryThread) => {
        setEditingThreadId(thread.id);
        setEditingTitle(thread.title?.trim() ?? '');
    };

    const handleCancelRename = () => {
        setEditingThreadId(null);
        setEditingTitle('');
    };

    const handleSaveRename = async (thread: AndieHistoryThread) => {
        const title = editingTitle.trim().replace(/\s+/g, ' ');
        const currentTitle = thread.title?.trim() ?? '';

        if (!title) {
            enqueueSnackbar(
                t('andie.chat.history.rename-empty', {
                    defaultValue: 'Enter a chat name.',
                }),
            );
            return;
        }

        if (title.length > CHAT_HISTORY_TITLE_MAX_LENGTH) {
            enqueueSnackbar(
                t('andie.chat.history.rename-too-long', {
                    defaultValue: 'Chat name is too long.',
                }),
            );
            return;
        }

        if (title === currentTitle) {
            handleCancelRename();
            return;
        }

        try {
            const result = await updateThreadTitle({
                variables: { threadId: thread.id, title },
                optimisticResponse: {
                    updateAndieChatThreadTitle: {
                        __typename: 'AndieChatThread',
                        id: thread.id,
                        title,
                        createdAt: thread.createdAt,
                        updatedAt: thread.updatedAt,
                    },
                },
            });

            if (result.errors?.length) {
                const message = result.errors[0]?.message ?? 'error.something-wrong';
                enqueueSnackbar(t(message, { defaultValue: CHAT_HISTORY_ERROR_FALLBACK }));
            } else {
                handleCancelRename();
            }
        } catch (renameError) {
            const message = renameError instanceof Error ? renameError.message : 'error.something-wrong';
            enqueueSnackbar(t(message, { defaultValue: CHAT_HISTORY_ERROR_FALLBACK }));
        }
    };

    return (
        <>
            <Stack sx={{ height: '100%', minHeight: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="flex-start" gap={1} px={2} py={1}>
                    <Stack direction="row" alignItems="center" gap={0.75} minWidth={0}>
                        <Tooltip
                            title={t('andie.chat.history.back', {
                                defaultValue: 'Back to chat',
                            })}
                        >
                            <IconButton
                                size="small"
                                aria-label={t('andie.chat.history.back', {
                                    defaultValue: 'Back to chat',
                                })}
                                onClick={onClose}
                                sx={{ flexShrink: 0 }}
                            >
                                <ArrowBackRounded fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                            {t('andie.chat.history.title', { defaultValue: 'Chat history' })}
                        </Typography>
                    </Stack>
                </Stack>
                <Divider />
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                    {loading && threads.length === 0 ? (
                        <Stack gap={1.25} p={2}>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <Stack key={index} gap={0.75}>
                                    <Skeleton variant="text" width="72%" />
                                    <Skeleton variant="text" width="38%" />
                                </Stack>
                            ))}
                        </Stack>
                    ) : null}

                    {error ? (
                        <Stack p={2}>
                            <Alert severity="warning" variant="outlined" sx={{ borderRadius: 1 }}>
                                {t('andie.chat.history.error', {
                                    defaultValue: 'Could not load chat history.',
                                })}
                            </Alert>
                        </Stack>
                    ) : null}

                    {!loading && !error && threads.length === 0 ? (
                        <Stack p={2} gap={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                                {t('andie.chat.history.empty.title', { defaultValue: 'No chats yet' })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('andie.chat.history.empty.description', {
                                    defaultValue:
                                        'Your Andie chats will appear here after the first message.',
                                })}
                            </Typography>
                        </Stack>
                    ) : null}

                    <List disablePadding>
                        {visibleThreads.map((thread) => {
                            const selected = thread.id === chatId;
                            const isEditing = editingThreadId === thread.id;

                            return (
                                <ListItem
                                    key={thread.id}
                                    disablePadding
                                    secondaryAction={
                                        isEditing ? undefined : (
                                            <Stack direction="row" alignItems="center" gap={0.25}>
                                                <Tooltip
                                                    title={t('str.rename', {
                                                        defaultValue: 'Rename',
                                                    })}
                                                >
                                                    <span>
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            aria-label={t('str.rename', {
                                                                defaultValue: 'Rename',
                                                            })}
                                                            disabled={updatingThreadTitle}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleStartRename(thread);
                                                            }}
                                                        >
                                                            <EditRounded fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip
                                                    title={t('andie.chat.history.delete', {
                                                        defaultValue: 'Delete chat',
                                                    })}
                                                >
                                                    <span>
                                                        <IconButton
                                                            edge="end"
                                                            size="small"
                                                            aria-label={t('andie.chat.history.delete', {
                                                                defaultValue: 'Delete chat',
                                                            })}
                                                            disabled={deletingThread}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                setThreadToDelete(thread);
                                                            }}
                                                        >
                                                            <DeleteOutlineRounded fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                        )
                                    }
                                >
                                    {isEditing ? (
                                        <Stack
                                            width="100%"
                                            minHeight={68}
                                            gap={0.5}
                                            px={2}
                                            py={1}
                                            sx={(theme) => ({
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                bgcolor: selected
                                                    ? alpha(theme.palette.andie.main, 0.12)
                                                    : 'transparent',
                                            })}
                                        >
                                            <Stack direction="row" alignItems="center" gap={0.5}>
                                                <TextField
                                                    autoFocus
                                                    fullWidth
                                                    size="small"
                                                    value={editingTitle}
                                                    placeholder={getThreadTitle(thread, t)}
                                                    inputProps={{
                                                        maxLength: CHAT_HISTORY_TITLE_MAX_LENGTH,
                                                        'aria-label': t('str.rename', {
                                                            defaultValue: 'Rename',
                                                        }),
                                                    }}
                                                    disabled={updatingThreadTitle}
                                                    onChange={(event) => setEditingTitle(event.target.value)}
                                                    onClick={(event) => event.stopPropagation()}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') {
                                                            event.preventDefault();
                                                            void handleSaveRename(thread);
                                                        }
                                                        if (event.key === 'Escape') {
                                                            event.preventDefault();
                                                            handleCancelRename();
                                                        }
                                                    }}
                                                />
                                                <Tooltip
                                                    title={t('str.action.escape.save', {
                                                        defaultValue: 'Save',
                                                    })}
                                                >
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={t('str.action.escape.save', {
                                                                defaultValue: 'Save',
                                                            })}
                                                            disabled={updatingThreadTitle}
                                                            onClick={() => {
                                                                void handleSaveRename(thread);
                                                            }}
                                                        >
                                                            <CheckRounded fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={t('str.action.escape.cancel')}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={t('str.action.escape.cancel')}
                                                            disabled={updatingThreadTitle}
                                                            onClick={handleCancelRename}
                                                        >
                                                            <CloseRounded fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {dateFormatter.format(new Date(thread.updatedAt))}
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <ListItemButton
                                            selected={selected}
                                            disabled={loadingMessages}
                                            onClick={() => handleSelectThread(thread)}
                                            sx={(theme) => ({
                                                minHeight: 68,
                                                pr: 9,
                                                py: 1,
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                '&.Mui-selected': {
                                                    bgcolor: alpha(theme.palette.andie.main, 0.12),
                                                },
                                                '&.Mui-selected:hover': {
                                                    bgcolor: alpha(theme.palette.andie.main, 0.18),
                                                },
                                            })}
                                        >
                                            <Stack minWidth={0} gap={0.5}>
                                                <Typography variant="body2" fontWeight={700} noWrap>
                                                    {getThreadTitle(thread, t)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    {dateFormatter.format(new Date(thread.updatedAt))}
                                                </Typography>
                                            </Stack>
                                        </ListItemButton>
                                    )}
                                </ListItem>
                            );
                        })}
                    </List>

                    {hasMoreThreads && !showAllThreads ? (
                        <Box px={2} py={1.25}>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setShowAllThreads(true)}
                                sx={{ justifyContent: 'flex-start', px: 0.5 }}
                            >
                                {`${t('andie.chat.history.view-all', { defaultValue: 'View all' })} (${threads.length})`}
                            </Button>
                        </Box>
                    ) : null}

                    {loadingMessages ? (
                        <Stack direction="row" alignItems="center" justifyContent="center" py={2}>
                            <CircularProgress size={22} />
                        </Stack>
                    ) : null}
                </Box>
            </Stack>

            <Dialog open={!!threadToDelete} onClose={() => setThreadToDelete(null)}>
                <DialogTitle>
                    {t('andie.chat.history.delete-title', { defaultValue: 'Delete chat?' })}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        {t('andie.chat.history.delete-description', {
                            defaultValue:
                                'This removes the selected chat and its messages from Andie history.',
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setThreadToDelete(null)} disabled={deletingThread}>
                        {t('str.action.escape.cancel')}
                    </Button>
                    <Button color="error" onClick={handleConfirmDelete} disabled={deletingThread}>
                        {deletingThread
                            ? t('str.loading', { defaultValue: 'Loading' })
                            : t('str.action.escape.delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AndieChatHistory;
